import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X, MessageCircle, Package, CreditCard, UserPlus, CheckCircle, AlertCircle, ClipboardCheck, PenTool } from 'lucide-react';

// 토스트 알림 컨테이너 컴포넌트
function ToastNotification({ notifications = [], onDismiss, onNotificationClick }) {
    return (
        <div style={styles.container}>
            {notifications.map((notification) => (
                <ToastItem
                    key={notification.id}
                    notification={notification}
                    onDismiss={onDismiss}
                    onClick={onNotificationClick}
                />
            ))}
        </div>
    );
}

// 개별 토스트 아이템
function ToastItem({ notification, onDismiss, onClick }) {
    const [isExiting, setIsExiting] = useState(false);
    const [progress, setProgress] = useState(100);

    // 자동 닫기 타이머
    useEffect(() => {
        const duration = notification.duration || 5000;
        const interval = 50;
        const decrement = (interval / duration) * 100;

        const progressTimer = setInterval(() => {
            setProgress((prev) => {
                if (prev <= 0) {
                    clearInterval(progressTimer);
                    return 0;
                }
                return prev - decrement;
            });
        }, interval);

        const dismissTimer = setTimeout(() => {
            handleDismiss();
        }, duration);

        return () => {
            clearInterval(progressTimer);
            clearTimeout(dismissTimer);
        };
    }, [notification.id]);

    const handleDismiss = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            onDismiss?.(notification.id);
        }, 300);
    }, [notification.id, onDismiss]);

    const handleClick = () => {
        onClick?.(notification);
        handleDismiss();
    };

    const getIcon = () => {
        switch (notification.type) {
            case 'order_created':
            case 'order_status_changed':
                return <Package size={20} />;
            // 치과 공정 3단계 알림 (2026-10-12): 의뢰서 확인 · 디자인 완료 · 포장 완료
            case 'order_received':
                return <ClipboardCheck size={20} />;
            case 'order_design_done':
                return <PenTool size={20} />;
            case 'case_packed':
                return <Package size={20} />;
            case 'chat_message':
                return <MessageCircle size={20} />;
            case 'payment_completed':
            case 'payment_approved':
            case 'payment_approval_request':
                return <CreditCard size={20} />;
            case 'staff_join_request':
            case 'staff_approved':
                return <UserPlus size={20} />;
            case 'success':
                return <CheckCircle size={20} />;
            case 'error':
                return <AlertCircle size={20} />;
            default:
                return <Bell size={20} />;
        }
    };

    const getColor = () => {
        switch (notification.type) {
            case 'order_created':
            case 'order_status_changed':
                return '#3b82f6';
            case 'order_received':      // 의뢰서 확인 (shared STAGE3_COLOR 와 같은 색)
                return '#4f46e5';
            case 'order_design_done':   // 디자인 완료
                return '#0b6e5d';
            case 'case_packed':         // 포장 완료
                return '#64748b';
            case 'chat_message':
                return '#8b5cf6';
            case 'payment_completed':
            case 'payment_approved':
                return '#10b981';
            case 'payment_approval_request':
            case 'staff_join_request':
                return '#f59e0b';
            case 'staff_approved':
            case 'success':
                return '#10b981';
            case 'error':
                return '#ef4444';
            default:
                return '#6366f1';
        }
    };

    const color = getColor();

    return (
        <div
            style={{
                ...styles.toast,
                ...(isExiting ? styles.toastExiting : {}),
                borderLeftColor: color,
            }}
            onClick={handleClick}
        >
            <div style={{ ...styles.iconWrapper, backgroundColor: `${color}15`, color }}>
                {getIcon()}
            </div>

            <div style={styles.content}>
                <div style={styles.title}>{notification.title}</div>
                <div style={styles.message}>{notification.message}</div>
                {notification.time && (
                    <div style={styles.time}>{notification.time}</div>
                )}
            </div>

            <button
                style={styles.closeButton}
                onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss();
                }}
            >
                <X size={16} />
            </button>

            {/* 진행 바 */}
            <div style={styles.progressBar}>
                <div
                    style={{
                        ...styles.progressFill,
                        width: `${progress}%`,
                        backgroundColor: color,
                    }}
                />
            </div>
        </div>
    );
}

// 토스트 알림 훅
export function useToastNotification() {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((notification) => {
        const id = notification.id || `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast = {
            ...notification,
            id,
            duration: notification.duration || 5000,
        };

        setToasts((prev) => {
            // 최대 5개까지만 표시
            const updated = [newToast, ...prev].slice(0, 5);
            return updated;
        });

        // 알림 소리 재생
        playNotificationSound();

        return id;
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    return {
        toasts,
        showToast,
        dismissToast,
        clearAllToasts,
    };
}

// 알림 소리 재생 (윈도우 호환성 개선)
let audioContext = null;

const playNotificationSound = () => {
    try {
        // AudioContext 재사용 (매번 새로 생성하지 않음)
        if (!audioContext) {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) {
                console.log('AudioContext 지원 안 됨');
                return;
            }
            audioContext = new AudioContextClass();
        }

        // suspended 상태면 resume 시도 (윈도우/Chrome 정책)
        if (audioContext.state === 'suspended') {
            audioContext.resume().catch(() => {});
        }

        // running 상태가 아니면 소리 재생 불가
        if (audioContext.state !== 'running') {
            console.log('AudioContext 상태:', audioContext.state);
            return;
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.1;

        const now = audioContext.currentTime;
        oscillator.start(now);

        // 페이드 아웃
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        oscillator.stop(now + 0.25);

    } catch (e) {
        // 소리 재생 실패 시 무시
        console.log('알림 소리 재생 실패:', e);
    }
};

// 사용자 상호작용 시 AudioContext 활성화 (윈도우 Chrome 정책 대응)
const initAudioContext = () => {
    if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume();
    }
};

// 첫 클릭/터치 시 AudioContext 활성화
if (typeof window !== 'undefined') {
    ['click', 'touchstart', 'keydown'].forEach(event => {
        document.addEventListener(event, initAudioContext, { once: true, passive: true });
    });
}

// 모바일 진동
export const vibrateNotification = () => {
    if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]);
    }
};

const styles = {
    container: {
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 10001,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxWidth: 380,
        width: 'calc(100vw - 32px)',
        pointerEvents: 'none',
    },
    toast: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 12,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        borderLeft: '4px solid',
        cursor: 'pointer',
        pointerEvents: 'auto',
        animation: 'slideIn 0.3s ease-out',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s, opacity 0.2s',
    },
    toastExiting: {
        animation: 'slideOut 0.3s ease-in forwards',
    },
    iconWrapper: {
        width: 40,
        height: 40,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    content: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        fontSize: 14,
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: 4,
        lineHeight: 1.4,
    },
    message: {
        fontSize: 13,
        color: '#6b7280',
        lineHeight: 1.5,
        wordBreak: 'break-word',
    },
    time: {
        fontSize: 11,
        color: '#9ca3af',
        marginTop: 6,
    },
    closeButton: {
        position: 'absolute',
        top: 8,
        right: 8,
        width: 24,
        height: 24,
        border: 'none',
        backgroundColor: 'transparent',
        color: '#9ca3af',
        cursor: 'pointer',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
    },
    progressBar: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 3,
        backgroundColor: '#f3f4f6',
    },
    progressFill: {
        height: '100%',
        transition: 'width 0.05s linear',
        borderRadius: '0 0 0 4px',
    },
};

// CSS 애니메이션 추가 (중복 방지)
if (typeof window !== 'undefined' && !document.getElementById('toast-notification-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'toast-notification-styles';
    styleSheet.textContent = `
        @keyframes slideIn {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }

        @keyframes slideOut {
            from {
                transform: translateX(0);
                opacity: 1;
            }
            to {
                transform: translateX(100%);
                opacity: 0;
            }
        }

        @media (max-width: 480px) {
            .toast-container {
                top: 8px !important;
                right: 8px !important;
                left: 8px !important;
                max-width: none !important;
                width: auto !important;
            }
        }
    `;
    document.head.appendChild(styleSheet);
}

export default ToastNotification;
